import { useQueryClient } from '@tanstack/solid-query';
import { type JSX, Show } from 'solid-js';
import { CrossBorderConsentSection } from '../components/CrossBorderConsentSection';
import { EmailBounceAlert } from '../components/EmailBounceAlert';
import { EmptyState } from '../components/EmptyState';
import { BrowserNotifications } from '../components/notifications/BrowserNotifications';
import { NotificationEstimate } from '../components/notifications/NotificationEstimate';
import { NotificationRecent } from '../components/notifications/NotificationRecent';
import { NotificationSound } from '../components/notifications/NotificationSound';
import { NotificationTiers } from '../components/notifications/NotificationTiers';
import { PageHeader } from '../components/PageHeader';
import { PushDevices } from '../components/PushDevices';
import { Toast } from '../components/Toast';
import { t } from '../i18n';
import { PUSH_SUBSCRIPTIONS_QUERY_KEY } from '../lib/pushSubscriptions';
import { SESSION_TOKEN } from '../lib/session';
import { createToast, NOTICE_TTL_MS } from '../lib/toast';

// The Notifications page: what reaches you, and where. Any signed-in member
// can use it. Top to bottom: presets, one card per tier, delivery timing, Send
// to with the test-email button after it, the browser section with its device
// list, Advanced, recent deliveries, and — for a CN person holding it — the
// cross-border consent's withdraw section (#5881). Slice 20 (#5705) mounts the timing group
// and the test email inside `NotificationTiers`, around its Send to block —
// after this component they would land below the browser slot — and each holds
// its own cycle over the same settings query. Slice 21 (#5706) fills two slots
// on the page: the weekly estimate under the header and the recent deliveries
// after Advanced. `NotificationTiers` owns the settings read and the one write;
// this route composes the browser section into it and owns the toast, which has
// to outlive the sections' re-reads.
//
// No icon in the header and none in the rail: `PageHeader` has no icon slot and
// a rail row carries none. The URL is `/account/notifications`, and sent mail
// links to it. `token` is injectable for tests, mirroring `EmailRoutes`; the
// router mounts it with no props → dev session.

export function Notifications(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session" and must
  // survive, or the signed-out branch can't be driven from a dev build.
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  // No token = prod pre-SSO: gate the whole route so per-user writes (push
  // register, setting changes) can never fire unauthenticated.
  const hasSession = token !== null;

  const queryClient = useQueryClient();
  // The browser section's switch subscribes or unsubscribes THIS browser;
  // refresh the device list so its row appears or disappears.
  const refreshDevices = (): void => {
    void queryClient.invalidateQueries({ queryKey: [PUSH_SUBSCRIPTIONS_QUERY_KEY] });
  };

  const notice = createToast(NOTICE_TTL_MS);
  const flash = (msg: string): void => notice.notify(msg);

  return (
    <section class="page notifications-settings">
      <PageHeader
        title={t('notifications_settings.title')}
        desc={t('notifications_settings.subtitle')}
        below={hasSession ? <NotificationEstimate token={token} /> : undefined}
      />

      <Show
        when={hasSession}
        fallback={<EmptyState message={t('notifications_settings.no_session')} />}
      >
        {/* First: a bouncing address means every email below it is dark. */}
        <EmailBounceAlert token={token} onNotice={flash} />

        <NotificationTiers
          token={token}
          onNotice={flash}
          browser={
            <>
              <BrowserNotifications onChange={refreshDevices} />
              <NotificationSound />
              <PushDevices token={token} onNotice={flash} />
            </>
          }
        />
        {/* Slice 21 (#5706), the second of its two slots: the recent list after
            Advanced — what the page emailed this reader and why, under the
            switches that decided it. */}
        <NotificationRecent token={token} />

        {/* Withdrawing the cross-border consent (#5881): paints only for a CN person holding it. */}
        <CrossBorderConsentSection token={token} onNotice={flash} />
      </Show>

      <Toast message={notice.toast()?.message ?? null} onDismiss={notice.dismiss} />
    </section>
  );
}
